import { api } from "../../api/client";
import type { FilterOption, FilterValues } from "../../api/types";
import type { DashboardChatMessage, DashboardDataset, DashboardRunResult, DashboardSummary } from "./types";

export function listDashboards(): Promise<DashboardSummary[]> {
  return api.get<DashboardSummary[]>("/api/dashboards");
}

function activeFilters(filters: FilterValues): FilterValues | undefined {
  const active: FilterValues = {};
  for (const [id, values] of Object.entries(filters)) {
    if (values.length > 0) active[id] = values;
  }
  return Object.keys(active).length > 0 ? active : undefined;
}

export function getDashboardFilterOptions(
  slug: string,
  filterId: string,
): Promise<FilterOption[]> {
  return api.get<FilterOption[]>(
    `/api/dashboards/${encodeURIComponent(slug)}/filters/${encodeURIComponent(filterId)}/options`,
  );
}

export function runDashboardDataset(
  slug: string,
  datasetId: string,
  dateFrom: string,
  dateTo: string,
  page: number,
  pageSize = 100,
  sortBy = "",
  sortDir: "asc" | "desc" | "" = "",
  filters: FilterValues = {},
  signal?: AbortSignal,
): Promise<DashboardDataset> {
  return api.post<DashboardDataset>(
    `/api/dashboards/${encodeURIComponent(slug)}/datasets/${encodeURIComponent(datasetId)}/run`,
    {
      date_from: dateFrom,
      date_to: dateTo,
      page,
      page_size: pageSize,
      sort_by: sortBy || undefined,
      sort_dir: sortDir || undefined,
      filters: activeFilters(filters),
    },
    { signal },
  );
}

export function runDashboard(
  slug: string,
  dateFrom?: string,
  dateTo?: string,
  filters: FilterValues = {},
  signal?: AbortSignal,
): Promise<DashboardRunResult> {
  return api.post<DashboardRunResult>(
    `/api/dashboards/${encodeURIComponent(slug)}/run`,
    {
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      filters: activeFilters(filters),
    },
    { signal },
  );
}

export function askDashboard(
  result: DashboardRunResult,
  messages: DashboardChatMessage[],
  t: (key: string) => string,
  signal?: AbortSignal,
): Promise<{ content: string }> {
  const definition = result.dashboard;
  const keys = new Set([definition.title_key, definition.description_key]);
  for (const filter of definition.filters ?? []) keys.add(filter.label_key);
  for (const widget of definition.widgets) {
    keys.add(widget.title_key);
    if (widget.subtitle_key) keys.add(widget.subtitle_key);
    for (const series of widget.series ?? []) keys.add(series.label_key);
  }
  if (definition.notice) keys.add(definition.notice.message_key);
  const tables: Record<string, { page: number; page_size: number; sort_by?: string; sort_dir?: string }> = {};
  for (const [id, dataset] of Object.entries(result.datasets)) {
    for (const column of dataset.columns) keys.add(`dashboards.columns.${column}`);
    if (dataset.page_size) {
      tables[id] = {
        page: dataset.page ?? 1,
        page_size: dataset.page_size,
        sort_by: dataset.sort_by,
        sort_dir: dataset.sort_dir,
      };
    }
  }
  // Keep complete user/assistant pairs within the server's history budget.
  let history = messages.slice(-21);
  while (history.length > 1 && new TextEncoder().encode(JSON.stringify(history)).length > 64 * 1024) {
    history = history.slice(2);
  }
  return api.post<{ content: string }>(
    `/api/dashboards/${encodeURIComponent(definition.slug)}/chat`,
    {
      date_from: result.date_from,
      date_to: result.date_to,
      filters: activeFilters(result.applied_filters ?? {}),
      tables,
      labels: Object.fromEntries([...keys].filter(Boolean).map((key) => [key, t(key)])),
      messages: history,
    },
    { signal },
  );
}
